import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_CONFIG } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import '../styles/pages/_enterprise-profile.scss';
import Button from '../components/Button';
import SelectDropdown from '../components/SelectDropdown';
import { ArrowLeft, Building2, Users, Calendar, CheckCircle, Search, Download, Filter } from 'lucide-react';

interface Branch {
  id: string;
  name: string;
  email: string;
  totalUsers: number;
  linkedDate: string;
  linkedBy: string;
}

interface EnterpriseProfile {
  id: string;
  name: string;
  email: string;
  createdAt: string;
  status: string;
  totalUsers: number;
  linkedDate?: string;
  parentEnterprise?: string;
  isParent: boolean;
  isSub: boolean;
  branches?: Branch[];
}

const EnterpriseProfilePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [profile, setProfile] = useState<EnterpriseProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'email' | 'users' | 'date'>('name');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      setError('');
      try {
        if (!token) {
          throw new Error('Authentication required');
        }

        const response = await fetch(`${API_CONFIG.BASE_URL}/api/enterprise-hierarchy/profile/${id}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || 'Failed to fetch enterprise profile');
        }

        const data = await response.json();
        if (!data.success || !data.data) {
          throw new Error('Invalid response from server');
        }
        setProfile(data.data);
      } catch (err: any) {
        setError(err.message || 'Failed to load profile');
        console.error('Error fetching profile:', err);
      } finally {
        setLoading(false);
      }
    };
    if (id && token) fetchProfile();
  }, [id, token]);

  // Filter and sort branches
  const filteredAndSortedBranches = useMemo(() => {
    if (!profile?.branches) return [];

    let filtered = profile.branches.filter(branch =>
      branch.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      branch.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'email':
          return a.email.localeCompare(b.email);
        case 'users':
          return b.totalUsers - a.totalUsers;
        case 'date':
          return new Date(b.linkedDate).getTime() - new Date(a.linkedDate).getTime();
        case 'name':
        default:
          return a.name.localeCompare(b.name);
      }
    });

    return filtered;
  }, [profile?.branches, searchTerm, sortBy]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedBranches.length / itemsPerPage);
  const paginatedBranches = filteredAndSortedBranches.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  if (loading) {
    return (
      <div className="enterprise-profile">
        <div className="enterprise-profile__loading">
          <div className="enterprise-profile__spinner"></div>
          <p>Loading enterprise profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="enterprise-profile">
        <div className="enterprise-profile__error-container">
          <div className="enterprise-profile__error">
            <h3>Error Loading Profile</h3>
            <p>{error}</p>
            <div className="enterprise-profile__error-actions">
              <button
                onClick={() => navigate(-1)}
                className="enterprise-profile__btn enterprise-profile__btn--secondary"
              >
                <ArrowLeft size={16} /> Go Back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="enterprise-profile">
        <div className="enterprise-profile__error-container">
          <div className="enterprise-profile__error">
            <h3>Enterprise Not Found</h3>
            <p>The requested enterprise profile could not be found.</p>
            <div className="enterprise-profile__error-actions">
              <button
                onClick={() => navigate(-1)}
                className="enterprise-profile__btn enterprise-profile__btn--secondary"
              >
                <ArrowLeft size={16} /> Go Back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="enterprise-profile">
      <div className="enterprise-profile__container">
        {/* Header */}
        <div className="enterprise-profile__header-section">
          <button
            onClick={() => navigate(-1)}
            className="enterprise-profile__back-btn"
            aria-label="Go back"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="enterprise-profile__title">{profile.name}</h1>
            <p className="enterprise-profile__subtitle">
              {profile.isParent ? 'Parent Enterprise' : profile.isSub ? 'Branch' : 'Enterprise'}
            </p>
          </div>
        </div>

        {/* Main Info Card */}
        <section className="enterprise-profile__main-card">
          <div className="enterprise-profile__card-header">
            <div className="enterprise-profile__card-title-section">
              <Building2 size={24} className="enterprise-profile__card-icon" />
              <div>
                <h2 className="enterprise-profile__card-title">Enterprise Information</h2>
              </div>
            </div>
          </div>

          <div className="enterprise-profile__info-grid">
            <div className="enterprise-profile__info-item">
              <label className="enterprise-profile__label">ENTERPRISE NAME</label>
              <div className="enterprise-profile__value">{profile.name}</div>
            </div>

            <div className="enterprise-profile__info-item">
              <label className="enterprise-profile__label">EMAIL ADDRESS</label>
              <div className="enterprise-profile__value">{profile.email}</div>
            </div>

            <div className="enterprise-profile__info-item">
              <label className="enterprise-profile__label">ENTERPRISE ID</label>
              <div className="enterprise-profile__value enterprise-profile__value--monospace">{profile.id}</div>
            </div>

            <div className="enterprise-profile__info-item">
              <label className="enterprise-profile__label">CREATED DATE</label>
              <div className="enterprise-profile__value">
                {new Date(profile.createdAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </div>
            </div>

            {profile.linkedDate && (
              <div className="enterprise-profile__info-item">
                <label className="enterprise-profile__label">LINKED DATE</label>
                <div className="enterprise-profile__value">
                  {new Date(profile.linkedDate).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </div>
              </div>
            )}

            <div className="enterprise-profile__info-item">
              <label className="enterprise-profile__label">TOTAL USERS</label>
              <div className="enterprise-profile__value">
                <Users size={16} className="enterprise-profile__value-icon" />
                {profile.totalUsers}
              </div>
            </div>

            {profile.parentEnterprise && (
              <div className="enterprise-profile__info-item">
                <label className="enterprise-profile__label">PARENT ENTERPRISE</label>
                <div className="enterprise-profile__value">{profile.parentEnterprise}</div>
              </div>
            )}

            <div className="enterprise-profile__info-item">
              <label className="enterprise-profile__label">ACCOUNT TYPE</label>
              <div className="enterprise-profile__value">
                {profile.isParent ? 'Parent Enterprise' : profile.isSub ? 'Branch / Sub-Enterprise' : 'Standalone'}
              </div>
            </div>
          </div>
        </section>

        {/* Branches Section (if parent) */}
        {profile.isParent && (
          <section className="enterprise-profile__branches-section">
            <div className="enterprise-profile__section-header">
              <div className="enterprise-profile__section-title-wrapper">
                <h2 className="enterprise-profile__section-title">Linked Branches</h2>
                <span className="enterprise-profile__badge-count">
                  {profile.branches?.length || 0}
                </span>
              </div>
              <p className="enterprise-profile__section-subtitle">
                Manage and view all sub-enterprises
              </p>
            </div>

            {profile.branches && profile.branches.length > 0 ? (
              <div className="enterprise-profile__branches-wrapper">
                {/* Controls */}
                <div className="enterprise-profile__controls">
                  <div className="enterprise-profile__search-box">
                    <Search size={18} className="enterprise-profile__search-icon" />
                    <input
                      type="text"
                      placeholder="Search by name or email..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="enterprise-profile__search-input"
                    />
                  </div>

                  <div className="enterprise-profile__controls-right">
                    <SelectDropdown
                      value={sortBy}
                      onChange={(value) => setSortBy(value as any)}
                      options={[
                        { value: 'name', label: 'Name' },
                        { value: 'email', label: 'Email' },
                        { value: 'users', label: 'Users (High to Low)' },
                        { value: 'date', label: 'Linked Date' }
                      ]}
                      placeholder="Sort by"
                      className="enterprise-profile__sort-select"
                      ariaLabel="Sort branches"
                    />
                  </div>
                </div>

                {/* Table */}
                <div className="enterprise-profile__table-wrapper">
                  <table className="enterprise-profile__table">
                    <thead className="enterprise-profile__table-head">
                      <tr>
                        <th className="enterprise-profile__table-cell">Branch Name</th>
                        <th className="enterprise-profile__table-cell">Email</th>
                        <th className="enterprise-profile__table-cell enterprise-profile__table-cell--center">Users</th>
                        <th className="enterprise-profile__table-cell">Linked Date</th>
                        <th className="enterprise-profile__table-cell">Type</th>
                        <th className="enterprise-profile__table-cell--action">Action</th>
                      </tr>
                    </thead>
                    <tbody className="enterprise-profile__table-body">
                      {paginatedBranches.map((branch) => (
                        <tr key={branch.id} className="enterprise-profile__table-row">
                          <td className="enterprise-profile__table-cell">
                            <div className="enterprise-profile__cell-content">
                              <Building2 size={16} className="enterprise-profile__cell-icon" />
                              <span className="enterprise-profile__cell-text">{branch.name}</span>
                            </div>
                          </td>
                          <td className="enterprise-profile__table-cell">
                            <span className="enterprise-profile__email-cell">{branch.email}</span>
                          </td>
                          <td className="enterprise-profile__table-cell enterprise-profile__table-cell--center">
                            <span className="enterprise-profile__users-badge">
                              {branch.totalUsers}
                            </span>
                          </td>
                          <td className="enterprise-profile__table-cell">
                            <span className="enterprise-profile__date-cell">
                              {new Date(branch.linkedDate).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })}
                            </span>
                          </td>
                          <td className="enterprise-profile__table-cell">
                            <span className={`enterprise-profile__type-badge enterprise-profile__type-badge--${branch.linkedBy}`}>
                              {branch.linkedBy === 'creation' ? 'Created' : 'Linked'}
                            </span>
                          </td>
                          <td className="enterprise-profile__table-cell--action">
                            <button
                              onClick={() => navigate(`/panel/enterprise/branch/${branch.id}`)}
                              className="enterprise-profile__view-details-btn"
                              title="View branch details"
                            >
                              View Details
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="enterprise-profile__pagination">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="enterprise-profile__pagination-btn"
                      aria-label="Previous page"
                    >
                      ← Previous
                    </button>

                    <div className="enterprise-profile__pagination-info">
                      Page <span className="enterprise-profile__pagination-current">{currentPage}</span> of{' '}
                      <span className="enterprise-profile__pagination-total">{totalPages}</span>
                    </div>

                    <button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="enterprise-profile__pagination-btn"
                      aria-label="Next page"
                    >
                      Next →
                    </button>
                  </div>
                )}

                {/* Results info */}
                <div className="enterprise-profile__results-info">
                  Showing {paginatedBranches.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0} to{' '}
                  {Math.min(currentPage * itemsPerPage, filteredAndSortedBranches.length)} of{' '}
                  {filteredAndSortedBranches.length} branches
                  {searchTerm && ` (filtered from ${profile.branches.length})`}
                </div>
              </div>
            ) : (
              <div className="enterprise-profile__empty-state">
                <Building2 size={48} className="enterprise-profile__empty-icon" />
                <h3>No Branches Linked Yet</h3>
                <p>Create or link new branches to expand your enterprise hierarchy.</p>
              </div>
            )}
          </section>
        )}

        {/* Actions */}
        <div className="enterprise-profile__actions">
          <button
            onClick={() => navigate(-1)}
            className="enterprise-profile__btn enterprise-profile__btn--secondary"
          >
            <ArrowLeft size={16} /> Back to Management
          </button>
        </div>
      </div>
    </div>
  );
};

export default EnterpriseProfilePage;
